import { useMemo } from 'react';
import FindingCalculationDetails from '../FindingCalculationDetails';
import { flattenFindings, severityClass } from './accountingConstants';

function FindingGuideBlock({ label, children }) {
  if (!children) return null;
  return (
    <div className="accounting-guide-block">
      <span className="accounting-guide-label">{label}</span>
      <p className="small mb-0">{children}</p>
    </div>
  );
}

/**
 * Shows one finding at a time so users review issues sequentially, not all at once.
 */
export default function AccountingFindingNavigator({
  sections = [],
  currentIndex,
  onIndexChange,
  expandedTechnicalKey,
  onToggleTechnical,
  onFix,
  renderFixWizard,
  activeFindingKey,
}) {
  const items = useMemo(() => flattenFindings(sections), [sections]);

  if (items.length === 0) {
    return null;
  }

  const safeIndex = Math.min(Math.max(0, currentIndex), items.length - 1);
  const current = items[safeIndex];
  const { finding, key, sectionLabel } = current;
  const isOpen = expandedTechnicalKey === key;
  const title = finding.plain_title || finding.title;
  const summary = finding.plain_summary || finding.description;

  return (
    <div className="admin-panel accounting-finding-navigator">
      <div className="accounting-finding-navigator__header">
        <div>
          <h5 className="mb-1">Review issues one by one</h5>
          <p className="small text-muted mb-0">
            Fix or note each item, then use Next. Re-run the scan when you are done with a batch of fixes.
          </p>
        </div>
        <div className="accounting-finding-navigator__counter" aria-live="polite">
          Issue <strong>{safeIndex + 1}</strong> of <strong>{items.length}</strong>
        </div>
      </div>

      <div className="accounting-finding-navigator__progress" aria-hidden="true">
        <div
          className="accounting-finding-navigator__progress-fill"
          style={{ width: `${((safeIndex + 1) / items.length) * 100}%` }}
        />
      </div>

      <div className="accounting-finding-navigator__section-label small text-muted">
        {sectionLabel}
      </div>

      <div className={`admin-alert-item ${severityClass(finding.severity)} accounting-finding-card`}>
        <div className="d-flex justify-content-between flex-wrap gap-2">
          <div className="flex-grow-1">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <strong>{title}</strong>
              <span className={`admin-badge audit-severity ${severityClass(finding.severity)}`}>
                {finding.severity_label || (finding.severity === 'critical' ? 'Must fix soon' : 'Review when you can')}
              </span>
            </div>
            {summary && <p className="small mb-2">{summary}</p>}
            <FindingCalculationDetails
              details={finding.calculation_details}
              severity={finding.severity}
            />
            <div className="accounting-finding-guide">
              <FindingGuideBlock label="What this means">{finding.what_it_means}</FindingGuideBlock>
              <FindingGuideBlock label="What to do">{finding.what_to_do}</FindingGuideBlock>
              {finding.likely_cause && (
                <FindingGuideBlock label="Likely cause">{finding.likely_cause}</FindingGuideBlock>
              )}
              {finding.root_cause_lines?.length > 0 && (
                <FindingGuideBlock label="Where the mismatch comes from">
                  <ul className="mb-0 ps-3">
                    {finding.root_cause_lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  {finding.root_cause_note && (
                    <p className="small text-muted mb-0 mt-1">{finding.root_cause_note}</p>
                  )}
                </FindingGuideBlock>
              )}
            </div>
            {finding.context && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mt-1"
                onClick={() => onToggleTechnical(isOpen ? null : key)}
              >
                {isOpen ? 'Hide technical details' : 'Show technical details (for support)'}
              </button>
            )}
            {isOpen && finding.context && (
              <pre className="audit-detail-pre mt-2 mb-0">{JSON.stringify(finding.context, null, 2)}</pre>
            )}
          </div>
          <div className="d-flex flex-column gap-1 align-items-stretch">
            {(finding.suggested_fixes || []).map((fix) => (
              <button
                key={fix.fix_type}
                type="button"
                className="btn btn-sm btn-success text-start"
                title={fix.plain_hint || ''}
                onClick={() => onFix(fix, finding, key)}
              >
                {fix.plain_label || fix.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeFindingKey === key && renderFixWizard?.()}

      <div className="accounting-finding-navigator__controls">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={safeIndex <= 0}
          onClick={() => onIndexChange(safeIndex - 1)}
        >
          ← Previous issue
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={safeIndex >= items.length - 1}
          onClick={() => onIndexChange(safeIndex + 1)}
        >
          Next issue →
        </button>
      </div>
    </div>
  );
}
